<template lang="pug">
el-dialog(
  title="Select Organization"
  top="5vh"
  width="60%"
  class="el-dialog__max-w-700"
  :close-on-click-modal="false"
  v-model="dialogVisible"
)
  .organizations-selector
    .organizations-selector-header
      el-input(
        v-model="searchTerm"
        placeholder="Search organizations"
        @input="debouncedSearch"
        clearable
      )
        template(#suffix)
          fai(type="fal" icon="search")

    .organizations-list(v-loading="loading")
      .empty-state(v-if="!organizations.length && !loading")
        p No organizations found

      .organizations-list-view(v-else)
        .organization-list-item(
          v-for="organization in organizations"
          :key="organization.id"
          :class="{ 'selected': selectedOrganization === organization.id }"
          @click="selectOrganization(organization.id)"
        )
          .organization-list-item-content
            .organization-thumbnail-small
              img(
                v-if="organization.avatar"
                :src="organization.avatar"
              )
              .no-image(v-else) No image
            .organization-details
              .organization-title {{ organization.name }}

    .pagination-controls(v-if="organizations.length")
      .simple-pagination.d-flex.align-items-center
        .page-nav.d-flex
          el-button(
            @click="handleCurrentChange(currentPage - 1)"
            :disabled="currentPage === 1 || loading"
            size="small"
          ) Prev
          .page-numbers.mx-2 {{ currentPage }}
          el-button(
            @click="handleCurrentChange(currentPage + 1)"
            :disabled="!hasMorePages || loading"
            size="small"
          ) Next
        .page-info.ms-3(v-if="organizations.length > 0")
          | Showing {{ startItem }}-{{ endItem }}

  template(#footer)
    .dialog-footer
      el-button(@click="handleCancel") Cancel
      el-button(type="primary" @click="handleConfirm" :disabled="!selectedOrganization") Confirm
</template>

<script lang="ts">
import {
  defineComponent, ref, computed, watch, onMounted,
} from 'vue';
import { ElMessage } from 'element-plus';
import { findOrganizationsV2 } from '@/graphs/gql';
import { makeRequest } from '@/graphs/index';

// Using a simple debounce implementation
function debounce(func: (...args: any[]) => void, wait: number) {
  let timeout: NodeJS.Timeout;
  return function (...args: any[]) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}

export default defineComponent({
  name: 'DialogSelectSingleOrganization',
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    preSelectedOrganization: {
      type: String,
      default: '',
    },
    excludeUnclaimed: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'confirm', 'clear'],
  setup(props, { emit }) {
    const dialogVisible = computed({
      get: () => props.modelValue,
      set: (value) => emit('update:modelValue', value),
    });

    const organizations = ref([]);
    const loading = ref(false);
    const searchTerm = ref('');
    const currentPage = ref(1);
    const pageSize = ref(20);
    const hasMorePages = ref(true);
    const selectedOrganization = ref(props.preSelectedOrganization || '');

    // Define fetchOrganizations function
    const fetchOrganizations = async () => {
      loading.value = true;
      try {
        const baseInput: any = {
          page: currentPage.value,
          pageSize: pageSize.value,
          query: searchTerm.value || undefined,
          statuses: 'Active',
        };

        // Add excludeUnclaimed filter if prop is true
        if (props.excludeUnclaimed) {
          baseInput.excludeUnclaimed = true;
        }

        const result = await makeRequest(
          'query',
          findOrganizationsV2,
          { baseInput },
          null,
        );

        if (result?.data?.findOrganizationsV2) {
          organizations.value = result.data.findOrganizationsV2;
          hasMorePages.value = result.data.findOrganizationsV2.length === pageSize.value;
        }
      } catch (error) {
        console.error('Error fetching organizations:', error);
        ElMessage.error('Failed to load organizations');
      } finally {
        loading.value = false;
      }
    };

    // Watch for dialog visibility to fetch data and reset selections when opened
    watch(() => props.modelValue, (isOpen) => {
      if (isOpen) {
        selectedOrganization.value = props.preSelectedOrganization || '';
        fetchOrganizations();
      }
    });

    // Watch for changes to preSelectedOrganization
    watch(() => props.preSelectedOrganization, (newVal) => {
      selectedOrganization.value = newVal || '';
    });

    const debouncedSearch = debounce(() => {
      currentPage.value = 1;
      hasMorePages.value = true;
      fetchOrganizations();
    }, 300);

    const handleCurrentChange = (page: number) => {
      if (page < 1 || (page > currentPage.value && !hasMorePages.value)) {
        return;
      }
      currentPage.value = page;
      fetchOrganizations();
    };

    const startItem = computed(() => {
      const offset = (currentPage.value - 1) * pageSize.value;
      return offset + 1;
    });

    const endItem = computed(() => {
      const offset = (currentPage.value - 1) * pageSize.value;
      return offset + organizations.value.length;
    });

    const selectOrganization = (id: string) => {
      selectedOrganization.value = id;
    };

    const handleConfirm = () => {
      const selectedOrganizationObject = organizations.value.find(
        (org: any) => org.id === selectedOrganization.value,
      );
      emit('confirm', selectedOrganization.value, selectedOrganizationObject);
      dialogVisible.value = false;
    };

    const handleCancel = () => {
      dialogVisible.value = false;
    };

    const handleClear = () => {
      emit('clear');
      selectedOrganization.value = '';
      dialogVisible.value = false;
    };

    onMounted(() => {
      if (props.modelValue) {
        fetchOrganizations();
      }
    });

    return {
      dialogVisible,
      organizations,
      loading,
      searchTerm,
      currentPage,
      pageSize,
      hasMorePages,
      selectedOrganization,
      startItem,
      endItem,
      debouncedSearch,
      handleCurrentChange,
      selectOrganization,
      handleConfirm,
      handleCancel,
      handleClear,
    };
  },
});
</script>

<style lang="scss" scoped>
.organizations-selector {
  display: flex;
  flex-direction: column;
  height: 60vh;
}

.organizations-selector-header {
  margin-bottom: 20px;
}

.organizations-list {
  flex: 1;
  overflow-y: auto;
  min-height: 300px;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #909399;
}

.organizations-list-view {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.organization-list-item {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 10px;
  cursor: pointer;
  position: relative;
  transition: all 0.3s;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.organization-list-item:hover {
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.organization-list-item.selected {
  border-color: #1EBEAF;
  background-color: rgba(30, 190, 175, 0.05);
}

.organization-list-item-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.organization-thumbnail-small {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 4px;
  flex-shrink: 0;
}

.organization-thumbnail-small img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.no-image {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f7fa;
  color: #909399;
  font-size: 10px;
  text-align: center;
  line-height: 1.2;
}

.organization-details {
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  min-width: 0;
}

.organization-title {
  font-weight: bold;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pagination-controls {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

.simple-pagination {
  display: flex;
  align-items: center;
}

.page-nav {
  display: flex;
  align-items: center;
}

.page-numbers {
  margin: 0 10px;
  font-weight: bold;
}

.page-info {
  margin-left: 15px;
  color: #606266;
  font-size: 0.9em;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
}
</style>
